/**
 * LSP server auto-detection and configuration resolution.
 */

import { execSync } from 'node:child_process';
import { existsSync } from 'node:fs';
import { join } from 'node:path';
import type { TraceMcpConfig } from '../config.js';
import { logger } from '../logger.js';

export interface LspServerSpec {
  language: string;
  command: string;
  args: string[];
  initializationOptions?: Record<string, unknown>;
  timeoutMs: number;
}

interface KnownServer {
  language: string;
  command: string;
  args: string[];
  detect: (rootPath: string) => boolean;
  initializationOptions?: Record<string, unknown>;
}

const KNOWN_SERVERS: KnownServer[] = [
  {
    language: 'typescript',
    command: 'npx',
    args: ['typescript-language-server', '--stdio'],
    detect: (root) =>
      existsSync(join(root, 'tsconfig.json')) || existsSync(join(root, 'package.json')),
    initializationOptions: {
      preferences: { includeInlayParameterNameHints: 'none' },
    },
  },
  {
    language: 'python',
    command: 'pyright-langserver',
    args: ['--stdio'],
    detect: (root) =>
      existsSync(join(root, 'pyproject.toml')) ||
      existsSync(join(root, 'requirements.txt')) ||
      existsSync(join(root, 'setup.py')),
  },
  {
    language: 'go',
    command: 'gopls',
    args: ['serve'],
    detect: (root) => existsSync(join(root, 'go.mod')),
  },
  {
    language: 'rust',
    command: 'rust-analyzer',
    args: [],
    detect: (root) => existsSync(join(root, 'Cargo.toml')),
  },
  {
    language: 'csharp',
    command: 'csharp-ls', // Roslyn-backed, stdio; `dotnet tool install -g csharp-ls`
    args: [],
    // ponytail: real gate is indexedLanguages.has('csharp') in resolveServers;
    // loose-.cs / no-project repos are caught by the supportsCallHierarchy skip in lifecycle.
    detect: () => true,
  },
];

/** Language extensions mapped to LSP language IDs */
export const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  '.ts': 'typescript',
  '.tsx': 'typescript',
  '.js': 'typescript', // tsserver handles JS too
  '.jsx': 'typescript',
  '.mts': 'typescript',
  '.cts': 'typescript',
  '.mjs': 'typescript',
  '.cjs': 'typescript',
  '.py': 'python',
  '.pyi': 'python',
  '.go': 'go',
  '.rs': 'rust',
  '.cs': 'csharp',
  '.csx': 'csharp',
};

/** Map trace-mcp file language string to LSP language ID */
export function fileLanguageToLspLanguage(fileLanguage: string | null): string | null {
  if (!fileLanguage) return null;
  const map: Record<string, string> = {
    typescript: 'typescript',
    javascript: 'typescript',
    python: 'python',
    go: 'go',
    rust: 'rust',
    csharp: 'csharp',
  };
  return map[fileLanguage] ?? null;
}

/** Check if a command is available on PATH */
function isCommandAvailable(command: string): boolean {
  try {
    execSync(`which ${command}`, { stdio: 'pipe', timeout: 5_000 });
    return true;
  } catch {
    return false;
  }
}

/**
 * Build specs from the user's explicit `lsp.servers` config entries.
 * Mutates `seen` so the caller's auto-detect pass won't re-add a language
 * the user already pinned explicitly.
 */
function resolveExplicitServers(config: TraceMcpConfig, seen: Set<string>): LspServerSpec[] {
  const specs: LspServerSpec[] = [];
  if (!config.lsp?.servers) return specs;

  for (const [language, serverConfig] of Object.entries(config.lsp.servers)) {
    if (seen.has(language)) continue;
    seen.add(language);

    if (!isCommandAvailable(serverConfig.command)) {
      logger.info(
        { language, command: serverConfig.command },
        'LSP server command not found, skipping',
      );
      continue;
    }

    specs.push({
      language,
      command: serverConfig.command,
      args: serverConfig.args ?? [],
      initializationOptions: serverConfig.initializationOptions as
        | Record<string, unknown>
        | undefined,
      timeoutMs: serverConfig.timeout_ms ?? 30_000,
    });
  }

  return specs;
}

/**
 * Build specs for auto-detected servers (known language servers whose
 * project markers are present and whose command resolves on PATH).
 * Skips any language already present in `seen` (explicit config wins).
 */
function resolveAutoDetectedServers(
  config: TraceMcpConfig,
  rootPath: string,
  indexedLanguages: Set<string>,
  seen: Set<string>,
): LspServerSpec[] {
  const specs: LspServerSpec[] = [];
  if (config.lsp?.auto_detect === false) return specs;

  for (const known of KNOWN_SERVERS) {
    if (seen.has(known.language)) continue;
    if (!indexedLanguages.has(known.language)) continue;
    if (!known.detect(rootPath)) continue;
    if (!isCommandAvailable(known.command)) {
      logger.debug(
        { language: known.language, command: known.command },
        'LSP server not installed',
      );
      continue;
    }

    seen.add(known.language);
    specs.push({
      language: known.language,
      command: known.command,
      args: known.args,
      initializationOptions: known.initializationOptions,
      timeoutMs: config.lsp?.servers?.[known.language]?.timeout_ms ?? 30_000,
    });
  }

  return specs;
}

/**
 * Resolve which LSP servers to start based on config + auto-detection.
 * Returns specs for servers that are both relevant and available.
 */
export function resolveServers(
  config: TraceMcpConfig,
  rootPath: string,
  indexedLanguages: Set<string>,
): LspServerSpec[] {
  const seen = new Set<string>();
  const explicit = resolveExplicitServers(config, seen);
  const autoDetected = resolveAutoDetectedServers(config, rootPath, indexedLanguages, seen);
  return [...explicit, ...autoDetected];
}
